import { lazyPage, type ModulePlugin } from "@keel/core";
import { lazy } from "react";
import { LoansTile } from "./Tile";

const load = () => import("./Loans");
const LoansList = lazyPage(load, "LoansList");
const NewLoan = lazyPage(load, "NewLoan");
const LoanPage = lazyPage(load, "LoanPage");
const LoanBrokers = lazyPage(load, "LoanBrokers");
const LoanTypes = lazyPage(() => import("./LoanTypes"), "LoanTypes");
const DocumentTemplates = lazyPage(() => import("./Documents"), "DocumentTemplates");
const applications = () => import("./Applications");
const LoanApplications = lazyPage(applications, "LoanApplications");
const LoanApplicationPage = lazyPage(applications, "LoanApplicationPage");
const PreContractPage = lazyPage(() => import("./PreContract"), "PreContractPage");
const LoanCollections = lazyPage(() => import("./Collections"), "LoanCollections");
const portfolio = () => import("./Portfolio");
const LoanPortfolio = lazyPage(portfolio, "LoanPortfolio");
const LoanReports = lazyPage(portfolio, "LoanReports");
const LoanRequests = lazyPage(() => import("./Requests"), "LoanRequests");
const LoanFunding = lazyPage(() => import("./Funding"), "LoanFunding");
const LoanTransfers = lazyPage(() => import("./Transfers"), "LoanTransfers");
const pools = () => import("./Pools");
const LoanPools = lazyPage(pools, "LoanPools");
const LoanPoolPage = lazyPage(pools, "LoanPoolPage");
const standing = () => import("./Standing");
const LoanBlacklist = lazyPage(standing, "LoanBlacklist");
const ConsentRegister = lazyPage(() => import("./Consents"), "ConsentRegister");
const ScorecardPage = lazyPage(() => import("./Scorecard"), "ScorecardPage");
const CustomerLoansTab = lazy(() => standing().then((m) => ({ default: m.CustomerLoansTab })));
const RetentionTab = lazy(() => import("./Retention").then((m) => ({ default: m.RetentionTab })));

/**
 * Loans & leasing (optional module), both directions: the company's own borrowing, and money it
 * lends to customers — applications, approval, payout and the loan book.
 */
const plugin: ModulePlugin = {
  key: "loans",
  records: { LoanAgreement: "/loans/", LoanApplication: "/loan-applications/" },
  nav: [
    {
      group: "Loans",
      items: [
        { to: "/loan-portfolio", label: "Portfolio", icon: "gauge", perm: "loans.agreements.read" },
        { to: "/loans", label: "Agreements", icon: "cash", perm: "loans.agreements.read" },
        {
          to: "/loan-applications",
          label: "Applications",
          icon: "file",
          perm: "loans.applications.read",
        },
        { to: "/loan-brokers", label: "Brokers", icon: "users", perm: "loans.agreements.read" },
        { to: "/loan-collections", label: "Collections", icon: "alert", perm: "loans.agreements.read" },
        { to: "/loan-requests", label: "Requests", icon: "comment", perm: "loans.agreements.read" },
        { to: "/loan-reports", label: "Reports", icon: "chart", perm: "loans.agreements.read" },
        { to: "/loan-funding", label: "Funding", icon: "wallet", perm: "loans.agreements.read" },
        { to: "/loan-transfers", label: "Transfers", icon: "send", perm: "loans.agreements.read" },
        { to: "/loan-pools", label: "Pools", icon: "grid", perm: "loans.agreements.read" },
        { to: "/loan-blacklist", label: "Blacklist", icon: "lock", perm: "loans.applications.read" },
        { to: "/loan-consents", label: "Consents", icon: "check", perm: "loans.applications.read" },
        { to: "/loan-scorecard", label: "Scorecard", icon: "sliders", perm: "loans.applications.read" },
        { to: "/loan-types", label: "Loan types", icon: "sliders", perm: "loans.agreements.read" },
        { to: "/loan-documents", label: "Documents", icon: "file", perm: "loans.agreements.read" },
      ],
    },
  ],
  routes: [
    { path: "/loans", element: <LoansList /> },
    { path: "/loans/new", element: <NewLoan /> },
    { path: "/loans/:id", element: <LoanPage /> },
    { path: "/loans/:id/edit", element: <NewLoan /> },
    { path: "/loan-types", element: <LoanTypes /> },
    { path: "/loan-scorecard", element: <ScorecardPage /> },
    { path: "/loan-documents", element: <DocumentTemplates /> },
    { path: "/loan-brokers", element: <LoanBrokers /> },
    { path: "/loan-applications", element: <LoanApplications /> },
    { path: "/loan-applications/:id", element: <LoanApplicationPage /> },
    { path: "/loan-applications/:id/pre-contract", element: <PreContractPage /> },
    { path: "/loan-collections", element: <LoanCollections /> },
    { path: "/loan-portfolio", element: <LoanPortfolio /> },
    { path: "/loan-reports", element: <LoanReports /> },
    { path: "/loan-requests", element: <LoanRequests /> },
    { path: "/loan-funding", element: <LoanFunding /> },
    { path: "/loan-pools", element: <LoanPools /> },
    { path: "/loan-pools/:id", element: <LoanPoolPage /> },
    { path: "/loan-transfers", element: <LoanTransfers /> },
    { path: "/loan-blacklist", element: <LoanBlacklist /> },
    { path: "/loan-consents", element: <ConsentRegister /> },
  ],
  commands: [
    ["New loan application", "/loan-applications", "plus", "loans.applications.write"],
    ["Loan applications", "/loan-applications", "file", "loans.applications.read"],
    ["Credit scorecard", "/loan-scorecard", "sliders", "loans.applications.read"],
    ["New loan agreement", "/loans/new", "plus", "loans.agreements.write"],
    ["Loans & leasing", "/loans", "cash", "loans.agreements.read"],
    ["Loan types", "/loan-types", "sliders", "loans.agreements.read"],
    ["Loan brokers", "/loan-brokers", "users", "loans.agreements.read"],
    ["Loan collections", "/loan-collections", "alert", "loans.agreements.read"],
    ["Loan portfolio", "/loan-portfolio", "gauge", "loans.agreements.read"],
    ["Loan reports", "/loan-reports", "chart", "loans.agreements.read"],
    ["Borrower requests", "/loan-requests", "comment", "loans.agreements.read"],
    ["Loan funding", "/loan-funding", "wallet", "loans.agreements.read"],
    ["Loan transfers", "/loan-transfers", "send", "loans.agreements.read"],
    ["Loan blacklist", "/loan-blacklist", "lock", "loans.applications.read"],
    ["Consent register", "/loan-consents", "check", "loans.applications.read"],
  ],
  newMenu: [
    {
      label: "Loan application",
      to: "/loan-applications?new=1",
      perm: "loans.applications.write",
      section: "Loans",
    },
  ],
  sources: { loan: "/loans/" },
  // The customer as the loan book sees them: standing, contracts, blacklist (ERP-98).
  partyTabs: [
    {
      key: "loans",
      label: "Loans",
      kinds: ["customers"],
      perm: "loans.agreements.read",
      component: CustomerLoansTab,
    },
  ],
  // How long lending's personal data is kept, and erasing it (ERP-127).
  settingsTabs: [
    {
      key: "loan-retention",
      label: "Data retention",
      perm: "loans.retention.manage",
      element: <RetentionTab />,
    },
  ],
  help: () => import("./help"),
  dashboardTiles: [{ key: "loans", section: "loans", perm: "loans.agreements.read", component: LoansTile }],
};
export default plugin;
/** The borrower portal (ERP-81): rendered by the app outside the staff sign-in. */
export const BorrowerPortal = lazyPage(() => import("./Portal"), "BorrowerPortal");
